import { useEffect, useState } from 'react';
import {
  Modal,
  Spinner,
  Alert,
  Badge,
  Card,
  Row,
  Col,
  ListGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faPhone,
  faEnvelope,
  faAward,
  faBriefcase,
  faMapPin,
  faFileDownload,
  faTimes 
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { getApplicantDetails } from 'features/booking/bookingApi';
import { viewDocument } from 'features/profile/profileApi';

interface ApplicantDetailModalProps {
  show: boolean;
  bookingId: number | null;
  onHide: () => void;
}

const ApplicantDetailModal = ({
  show,
  bookingId,
  onHide
}: ApplicantDetailModalProps) => {
  const [applicant, setApplicant] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && bookingId) {
      fetchApplicantDetails();
    }
  }, [show, bookingId]);

  const fetchApplicantDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getApplicantDetails(bookingId!);
      setApplicant(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'danger';
      case 'completed':
        return 'info';
      default:
        return 'secondary';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton className="border-bottom border-translucent">
        <Modal.Title className="fs-5 fw-bold">Applicant Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" className="text-primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            {error}
          </Alert>
        ) : applicant ? (
          <>
            {/* Booking Status */}
            <div className="mb-4 p-3 bg-body-secondary rounded-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-body-secondary fs-9 fw-bold text-uppercase">
                  Booking Status
                </span>
                <Badge bg={getStatusVariant(applicant.status)} className="fs-9">
                  {applicant.status.charAt(0).toUpperCase() +
                    applicant.status.slice(1)}
                </Badge>
              </div>
              <small className="text-body-tertiary">
                Booked on:{' '}
                {new Date(applicant.booked_at).toLocaleDateString('en-GB')}
              </small>
            </div>

            {/* Locum Profile Section */}
            <div className="mb-4">
              <h6 className="fw-bold mb-3 text-uppercase fs-8">
                <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                Personal Information
              </h6>

              <Row className="g-3">
                <Col md={6}>
                  <Card className="border border-translucent">
                    <Card.Body className="p-3">
                      <small className="text-body-secondary fw-bold">
                        Name
                      </small>
                      <p className="mb-0 fw-semibold">{applicant.name}</p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border border-translucent">
                    <Card.Body className="p-3">
                      <small className="text-body-secondary fw-bold">
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                        Email
                      </small>
                      <p className="mb-0">
                        <a
                          href={`mailto:${applicant.email}`}
                          className="text-decoration-none"
                        >
                          {applicant.email}
                        </a>
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border border-translucent">
                    <Card.Body className="p-3">
                      <small className="text-body-secondary fw-bold">
                        <FontAwesomeIcon icon={faPhone} className="me-2" />
                        Phone
                      </small>
                      <p className="mb-0">{applicant.phone || 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border border-translucent">
                    <Card.Body className="p-3">
                      <small className="text-body-secondary fw-bold">
                        <FontAwesomeIcon icon={faMapPin} className="me-2" />
                        Location
                      </small>
                      <p className="mb-0">{applicant.location || 'N/A'}</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Qualifications Section */}
            <div className="mb-4">
              <h6 className="fw-bold mb-3 text-uppercase fs-8">
                <FontAwesomeIcon icon={faAward} className="me-2 text-success" />
                Qualifications & Experience
              </h6>

              <Row className="g-3">
                <Col md={6}>
                  <Card className="border border-translucent">
                    <Card.Body className="p-3">
                      <small className="text-body-secondary fw-bold">
                        Qualifications
                      </small>
                      <p className="mb-0 text-body-emphasis">
                        {applicant.qualifications || 'Not specified'}
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border border-translucent">
                    <Card.Body className="p-3">
                      <small className="text-body-secondary fw-bold">
                        <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                        Experience
                      </small>
                      <p className="mb-0">
                        {applicant.experience_years || 0} years
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={12}>
                  <Card className="border border-translucent">
                    <Card.Body className="p-3">
                      <small className="text-body-secondary fw-bold">
                        Locum Role
                      </small>
                      <p className="mb-0">
                        <Badge bg="primary" className="fs-9">
                          {applicant.locum_role || 'N/A'}
                        </Badge>
                      </p>
                    </Card.Body>
                  </Card>
                </Col>

                {applicant.travel_distance && (
                  <Col md={12}>
                    <Card className="border border-translucent">
                      <Card.Body className="p-3">
                        <small className="text-body-secondary fw-bold">
                          Travel Distance
                        </small>
                        <p className="mb-0">
                          Up to {applicant.travel_distance} km
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </Row>
            </div>

            {/* Shift Information */}
            <div className="mb-4">
              <h6 className="fw-bold mb-3 text-uppercase fs-8">
                Shift Information
              </h6>

              <Card className="border border-translucent">
                <Card.Body className="p-3">
                  <Row className="g-3">
                    <Col md={6}>
                      <small className="text-body-secondary fw-bold">
                        Date
                      </small>
                      <p className="mb-0">
                        {new Date(applicant.date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </Col>

                    <Col md={6}>
                      <small className="text-body-secondary fw-bold">
                        Time
                      </small>
                      <p className="mb-0">
                        {applicant.start_time.slice(0, 5)} -{' '}
                        {applicant.end_time.slice(0, 5)}
                      </p>
                    </Col>

                    <Col md={6}>
                      <small className="text-body-secondary fw-bold">
                        Location
                      </small>
                      <p className="mb-0">{applicant.shift_location}</p>
                    </Col>

                    <Col md={6}>
                      <small className="text-body-secondary fw-bold">
                        Pay Rate
                      </small>
                      <p className="mb-0 fw-semibold">£{applicant.pay_rate}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>

            {/* Documents Section */}
            {applicant.documents && applicant.documents.length > 0 && (
              <div className="mb-4">
                <h6 className="fw-bold mb-3 text-uppercase fs-8">
                  <FontAwesomeIcon
                    icon={faFileDownload}
                    className="me-2 text-info"
                  />
                  Documents ({applicant.documents.length})
                </h6>

                <ListGroup className="border-0">
                  {applicant.documents.map((doc: any) => (
                    <ListGroup.Item
                      key={doc.id}
                      className="d-flex justify-content-between align-items-center bg-body-secondary border-translucent"
                    >
                      <div>
                        <small className="text-body-secondary fw-bold d-block mb-1">
                          {doc.doc_type.toUpperCase()}
                        </small>
                        <span className="text-body-emphasis">
                          {doc.document_name}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="phoenix-primary"
                        onClick={() => viewDocument(doc.document_name)}
                      >
                        <FontAwesomeIcon
                          icon={faFileDownload}
                          className="me-1"
                        />
                        View
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>

      <Modal.Footer className="border-top border-translucent">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApplicantDetailModal;
